import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { useSession } from '../state/session.js';

export function RegisterPage() {
  const navigate = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const bundle = await register(form);
      setSession(bundle);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create your account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-svh max-w-mobile flex-col px-6 py-10">
      <div className="mb-6">
        <p className="font-display text-3xl font-bold tracking-tight">Create account</p>
        <p className="mt-1 text-sm text-muted">A few details and you're set.</p>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Full name</span>
          <input className="input" value={form.name} onChange={update('name')} required minLength={2} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Email</span>
          <input type="email" className="input" value={form.email} onChange={update('email')} required />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Phone</span>
          <input
            type="tel"
            className="input"
            placeholder="+923001234567"
            value={form.phone}
            onChange={update('phone')}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Password</span>
          <input
            type="password"
            className="input"
            value={form.password}
            onChange={update('password')}
            required
            minLength={8}
          />
        </label>
        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}
        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? 'Creating…' : 'Create account'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-ink">
          Sign in
        </Link>
      </p>
    </div>
  );
}
